import {
  getMasterKeyLocal,
  putMasterKeyLocal,
  clearMasterKeyLocal,
} from "@/lib/db";
import { isAndroidPlatform } from "@/utils/platform";

interface StoredMasterKeyRecord {
  version: 2;
  userId: string;
  keyBase64: string;
  storedAt: string;
}

const WEB_KEY_PREFIX = "birch_master_key_v2_";
const LEGACY_WEB_KEY = "birch_master_key";

function isStoredRecord(value: unknown): value is StoredMasterKeyRecord {
  if (!value || typeof value !== "object") return false;

  const candidate = value as Partial<StoredMasterKeyRecord>;
  return (
    candidate.version === 2 &&
    typeof candidate.userId === "string" &&
    candidate.userId.length > 0 &&
    typeof candidate.keyBase64 === "string" &&
    candidate.keyBase64.length > 0 &&
    typeof candidate.storedAt === "string"
  );
}

function parseRecord(raw: string | null | undefined): StoredMasterKeyRecord | null {
  if (!raw) return null;

  try {
    const parsed: unknown = JSON.parse(raw);
    return isStoredRecord(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

export async function getMasterKeyForUser(
  userId: string
): Promise<string | undefined> {
  if (!userId) return undefined;

  if (isAndroidPlatform()) {
    const record = parseRecord(await getMasterKeyLocal());
    if (!record || record.userId !== userId) return undefined;
    return record.keyBase64;
  }

  if (typeof sessionStorage === "undefined") return undefined;

  const record = parseRecord(
    sessionStorage.getItem(`${WEB_KEY_PREFIX}${userId}`)
  );

  if (!record || record.userId !== userId) return undefined;
  return record.keyBase64;
}

export async function putMasterKeyForUser(
  userId: string,
  keyBase64: string
): Promise<void> {
  if (!userId || !keyBase64) {
    throw new Error("A user ID and master key are required");
  }

  const record: StoredMasterKeyRecord = {
    version: 2,
    userId,
    keyBase64,
    storedAt: new Date().toISOString(),
  };

  const encoded = JSON.stringify(record);

  if (isAndroidPlatform()) {
    await putMasterKeyLocal(encoded);
    return;
  }

  if (typeof sessionStorage !== "undefined") {
    sessionStorage.setItem(`${WEB_KEY_PREFIX}${userId}`, encoded);
    sessionStorage.removeItem(LEGACY_WEB_KEY);
  }
}

export async function clearMasterKeyForUser(
  userId?: string
): Promise<void> {
  if (isAndroidPlatform()) {
    await clearMasterKeyLocal();
    return;
  }

  if (typeof sessionStorage === "undefined") return;

  sessionStorage.removeItem(LEGACY_WEB_KEY);

  if (userId) {
    sessionStorage.removeItem(`${WEB_KEY_PREFIX}${userId}`);
    return;
  }

  const keysToRemove: string[] = [];

  for (let index = 0; index < sessionStorage.length; index += 1) {
    const key = sessionStorage.key(index);
    if (key?.startsWith(WEB_KEY_PREFIX)) {
      keysToRemove.push(key);
    }
  }

  for (const key of keysToRemove) {
    sessionStorage.removeItem(key);
  }
}

// ----------------------------------------------------
// Shared in-memory singleton store for the E2E CryptoKey
// ----------------------------------------------------
let _cryptoKey: CryptoKey | null = null;
let _userId: string | null = null;

/** Called by useEncryption whenever the master key is loaded, unlocked, or cleared. */
export function setE2ECryptoKey(key: CryptoKey | null, userId: string | null): void {
  _cryptoKey = key;
  _userId = userId;
}

/** Returns the current E2E master key, or null if not yet unlocked. */
export function getE2ECryptoKey(expectedUserId?: string): CryptoKey | null {
  if (expectedUserId && _userId !== expectedUserId) {
    return null;
  }
  return _cryptoKey;
}

/** Returns the userId associated with the current key, or null. */
export function getE2ECryptoKeyUserId(): string | null {
  return _userId;
}
